<template>
    <div class="page-shell">
        <p class="mobile-page-title font-helvetica-700 font-bold text-gray-dark lg:text-[40px] lg:uppercase">Профиль</p>
        <SettingsNavBar />
        <div class="flex w-full items-start gap-3 rounded-[20px] bg-white-gray px-5 py-4 text-extra-text-color">
            <SvgoIconComponentSIconInfo class="mt-[3px] h-[14px] w-[14px] shrink-0 text-extra-text-color" aria-hidden="true"></SvgoIconComponentSIconInfo>
            <p class="font-helvetica-400 font-normal text-[16px]">
                Для изменения реквизитов обратитесь к своему менеджеру.
            </p>
        </div>

        <div class="flex w-full min-w-0 flex-col gap-4 lg:hidden">
            <section class="rounded-[20px] border border-gray-border bg-white px-5 py-5">
                <p class="text-[18px] font-helvetica-700 text-gray-dark">Партнер и представитель</p>
                <div class="mt-4 flex flex-col gap-4 text-gray-dark">
                    <div class="flex flex-col gap-1">
                        <span class="text-[15px] text-extra-text-color">Контрагент</span>
                        <span class="text-[18px] font-helvetica-700">{{ mobileProfile.partner }}</span>
                    </div>
                    <div class="flex flex-col gap-1">
                        <span class="text-[15px] text-extra-text-color">Представитель</span>
                        <span class="text-[18px] font-helvetica-700">{{ mobileProfile.contact }}</span>
                    </div>
                </div>
            </section>

            <section class="rounded-[20px] border border-gray-border bg-white px-5 py-5">
                <div class="flex items-start justify-between gap-4">
                    <p class="text-[18px] font-helvetica-700 text-gray-dark">Ваш менеджер</p>
                    <span
                        v-if="hasPdz"
                        class="rounded-full bg-selected-filter px-3 py-1 text-[12px] font-helvetica-700 uppercase tracking-[0.06em] text-orange"
                    >
                        ПДЗ
                    </span>
                </div>

                <div class="mt-4 flex flex-col gap-4 text-gray-dark">
                    <div class="flex flex-col gap-1">
                        <span class="text-[15px] text-extra-text-color">Менеджер</span>
                        <span class="text-[18px] font-helvetica-700">{{ mobileProfile.manager.name }}</span>
                    </div>
                    <div class="flex flex-col gap-1">
                        <span class="text-[15px] text-extra-text-color">Телефон</span>
                        <span class="text-[18px] font-helvetica-700">{{ mobileProfile.manager.phone }}</span>
                    </div>
                    <div class="flex flex-col gap-1">
                        <span class="text-[15px] text-extra-text-color">E-mail</span>
                        <span class="text-[18px] font-helvetica-700 break-all">{{ mobileProfile.manager.email }}</span>
                    </div>
                    <div v-if="hasPdz" class="flex flex-col gap-1">
                        <span class="text-[15px] text-extra-text-color">Просроченная задолженность</span>
                        <span class="text-[22px] font-helvetica-700 text-orange">{{ formattedPdz }}</span>
                    </div>
                </div>
            </section>

            <section class="rounded-[20px] border border-gray-border bg-white px-5 py-5">
                <p class="text-[18px] font-helvetica-700 text-gray-dark">Реквизиты</p>
                <div class="mt-4 flex flex-col gap-4 text-gray-dark">
                    <div v-for="row in mobileProfile.requisites" :key="row.label" class="flex flex-col gap-1">
                        <span class="text-[15px] text-extra-text-color">{{ row.label }}</span>
                        <span class="text-[18px] font-helvetica-700">{{ row.value }}</span>
                    </div>
                </div>
            </section>
        </div>

        <div class="hidden w-full min-w-0 flex-col gap-6 lg:flex">
            <div v-for="section in profileSections" :key="section.title" class="flex min-w-0 flex-col gap-3 xl:flex-row xl:gap-8 2xl:gap-10">
                <div class="w-full shrink-0 pt-1 text-[18px] font-helvetica-700 font-bold text-gray-dark xl:w-[170px]">
                    {{ section.title }}
                </div>
                <div class="flex flex-1 flex-col gap-2">
                    <div
                      v-for="row in section.rows"
                      :key="row.label"
                      :class="
                        row.emphasis
                          ? 'rounded-[16px] border border-orange/20 bg-warning-soft px-4 py-3'
                          : 'flex min-w-0 flex-col gap-1 xl:flex-row xl:items-end xl:gap-5'
                      "
                    >
                        <div v-if="row.emphasis" class="flex min-w-0 flex-col gap-1.5 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
                            <span class="text-[15px] font-helvetica-700 font-bold uppercase tracking-[0.04em] text-orange">
                                {{ row.label }}
                            </span>
                            <span class="text-[20px] font-helvetica-700 font-bold text-orange">
                                {{ row.value }}
                            </span>
                        </div>
                        <template v-else>
                          <div class="flex w-full max-w-[320px] items-end gap-4">
                              <span class="text-[18px] font-helvetica-400 font-normal text-extra-text-color">
                                  {{ row.label }}
                              </span>
                              <span class="h-px flex-1 bg-gray-border"></span>
                          </div>
                          <span class="text-[18px] font-helvetica-400 font-normal text-gray-dark">
                              {{ row.value }}
                          </span>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import SettingsNavBar from "~/components/settings/SettingsNavBar.vue";
import { computed, onMounted, ref } from "vue";
import { useNuxtApp } from "#imports";
import { useClientPdz } from "~/composables/useClientPdz";

usePageTitle("Профиль: ваши данные");

type ProfileRow = {
    label: string;
    value: string;
    emphasis?: boolean;
};

type ProfileSection = {
    title: string;
    rows: ProfileRow[];
};

type ConditionsResponse = {
    Контрагент?: string;
    Договор?: string;
    Условия?: string;
    КонтактноеЛицо?: string;
    ТипЦен?: string;
    Баланс?: string;
    СуммаКредита?: string;
    Менеджер?: string;
    Реквизиты?: string;
    API_key?: string;
};

const conditions = ref<ConditionsResponse | null>(null);
const conditionsLoading = ref(true);
const { hasPdz, formattedPdz, fetchPdz } = useClientPdz();

const nuxtApp = useNuxtApp();
const $useOneCApi = nuxtApp.$useOneCApi as (action: string, payload?: Record<string, any>) => Promise<any>;

const displayValue = (value: unknown) => {
    const text = value === null || value === undefined ? "" : String(value).trim();
    return text || "—";
};

const splitLines = (value?: string) => {
    if (!value) return [];
    return value
        .replace(/<br\s*\/?>/gi, "\n")
        .split("\n")
        .map((line) => line.replace(/<[^>]*>/g, "").trim())
        .filter(Boolean);
};

const pickByPrefix = (lines: string[], prefix: string) => {
    const line = lines.find((item) => item.toLowerCase().startsWith(prefix.toLowerCase()));
    if (!line) return "";
    const parts = line.split(":");
    return parts.length > 1 ? parts.slice(1).join(":").trim() : "";
};

const parseManager = (value?: string) => {
    const lines = splitLines(value);
    const name = lines[0] ? lines[0].replace(/,$/, "").trim() : "";
    const phone = pickByPrefix(lines, "тел.") || pickByPrefix(lines, "телефон");
    const email = pickByPrefix(lines, "e-mail");
    return { name, phone, email };
};

const parseRequisites = (value?: string) => {
    const lines = splitLines(value);
    return {
        name: lines[0] || "",
        inn: pickByPrefix(lines, "инн/кпп"),
        legal: pickByPrefix(lines, "юр. адрес") || pickByPrefix(lines, "юр.адрес"),
        fact: pickByPrefix(lines, "факт. адрес") || pickByPrefix(lines, "факт.адрес"),
        phone: pickByPrefix(lines, "телефоны") || pickByPrefix(lines, "телефон"),
        email: pickByPrefix(lines, "e-mail"),
    };
};

const fetchConditions = async () => {
    conditionsLoading.value = true;
    try {
        const res = await $useOneCApi("getConditions");
        conditions.value = res ?? null;
    } catch (error) {
        console.error("Не удалось загрузить условия", error);
        conditions.value = null;
    } finally {
        conditionsLoading.value = false;
    }
};

onMounted(() => {
    fetchConditions();
    fetchPdz();
});

const profileSections = computed<ProfileSection[]>(() => {
    const data = conditions.value ?? {};
    const contactLines = splitLines(data.КонтактноеЛицо);
    const contactName = contactLines[0] || "";
    const manager = parseManager(data.Менеджер);
    const requisites = parseRequisites(data.Реквизиты);
    return [
        {
            title: "Партнёр:",
            rows: [
                { label: "Контрагент", value: displayValue(data.Контрагент) },
            ],
        },
        {
            title: "Представитель:",
            rows: [
                { label: "Контактное Лицо", value: displayValue(contactName || data.КонтактноеЛицо) },
            ],
        },
        {
            title: "Ваш менеджер:",
            rows: [
                { label: "ФИО", value: displayValue(manager.name || data.Менеджер) },
                { label: "Телефон", value: displayValue(manager.phone) },
                { label: "E-Mail", value: displayValue(manager.email) },
                ...(hasPdz.value
                    ? [{ label: "ПДЗ", value: formattedPdz.value, emphasis: true }]
                    : []),
            ],
        },
        {
            title: "Реквизиты:",
            rows: [
                { label: "Контрагент", value: displayValue(requisites.name || data.Реквизиты) },
                { label: "ИНН/КПП", value: displayValue(requisites.inn) },
                { label: "Юр.адрес", value: displayValue(requisites.legal) },
                { label: "Факт.адрес", value: displayValue(requisites.fact) },
                { label: "Телефон", value: displayValue(requisites.phone) },
                { label: "E-Mail", value: displayValue(requisites.email) },
            ],
        },
    ];
});

const mobileProfile = computed(() => {
    const data = conditions.value ?? {};
    const contactLines = splitLines(data.КонтактноеЛицо);
    const manager = parseManager(data.Менеджер);
    const requisites = parseRequisites(data.Реквизиты);

    return {
        partner: displayValue(data.Контрагент),
        contact: displayValue(contactLines[0] || data.КонтактноеЛицо),
        manager: {
            name: displayValue(manager.name || data.Менеджер),
            phone: displayValue(manager.phone),
            email: displayValue(manager.email),
        },
        requisites: [
            { label: "ИНН/КПП", value: displayValue(requisites.inn) },
            { label: "Юр. адрес", value: displayValue(requisites.legal) },
            { label: "Факт. адрес", value: displayValue(requisites.fact) },
            { label: "Телефон", value: displayValue(requisites.phone) },
            { label: "E-mail", value: displayValue(requisites.email) },
        ],
    };
});
</script>
